import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGenerateLessonTranscriptMutation,
  useLazyGetLessonTranscriptQuery,
} from "../services/users/ai";
import { useAiPoll } from "./useAiPoll";
import { toastAiError } from "../utils/aiErrors";

/**
 * Fetch / generate / poll lesson transcript status (no text display).
 *
 * @param {string|number|null|undefined} lessonId
 * @param {{ enabled?: boolean }} options
 */
export function useLessonTranscript(lessonId, { enabled = true } = {}) {
  const { t } = useTranslation("ai");
  const [getTranscript] = useLazyGetLessonTranscriptQuery();
  const [generateTranscript, { isLoading: isGenerating }] =
    useGenerateLessonTranscriptMutation();

  const [status, setStatus] = useState("loading"); // loading | not_generated | pending | completed
  const [shouldPoll, setShouldPoll] = useState(false);

  const applyData = useCallback((data) => {
    if (!data) return;
    if (data.status === "completed") {
      setStatus("completed");
      setShouldPoll(false);
    } else if (data.status === "pending") {
      setStatus("pending");
      setShouldPoll(true);
    } else if (data.status === "not_generated") {
      setStatus("not_generated");
    }
  }, []);

  const load = useCallback(async () => {
    if (!lessonId) return;
    setStatus("loading");
    try {
      const result = await getTranscript(lessonId);
      if (result.error) {
        if (result.error.status === 404) {
          setStatus("not_generated");
          return;
        }
        toastAiError(result.error, t);
        setStatus("not_generated");
        return;
      }
      applyData(result.data);
    } catch (err) {
      toastAiError(err, t);
      setStatus("not_generated");
    }
  }, [applyData, getTranscript, lessonId, t]);

  useEffect(() => {
    if (!enabled || !lessonId) {
      setStatus("loading");
      setShouldPoll(false);
      return;
    }
    setShouldPoll(false);
    load();
  }, [lessonId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchForPoll = useCallback(async () => {
    const result = await getTranscript(lessonId);
    if (result.error) {
      if (result.error.status === 404) return { status: "pending" };
      throw result.error;
    }
    return result.data;
  }, [getTranscript, lessonId]);

  const {
    isPolling,
    timedOut,
    start: startPoll,
  } = useAiPoll(fetchForPoll, {
    enabled: enabled && shouldPoll,
    onCompleted: (data) => applyData(data),
    onError: (err) => {
      setShouldPoll(false);
      toastAiError(err, t);
      setStatus("not_generated");
    },
    onTimeout: () => {
      setShouldPoll(false);
      setStatus("pending");
    },
  });

  const generate = useCallback(async () => {
    if (!lessonId) return;
    try {
      const result = await generateTranscript(lessonId);
      if (result.error) {
        toastAiError(result.error, t);
        return;
      }
      setStatus("pending");
      setShouldPoll(true);
      startPoll();
    } catch (err) {
      toastAiError(err, t);
    }
  }, [generateTranscript, lessonId, startPoll, t]);

  return {
    status,
    isReady: status === "completed",
    isGenerating,
    isPolling,
    timedOut,
    generate,
    retry: generate,
  };
}

export default useLessonTranscript;
